import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FUEL_TYPE_LABELS, ACK_STATUS_LABELS, type FuelType } from '@shared/domain';
import { formatINR, maxQuantityForAmount, quantityToWire, parseQuantity, amountForQuantity } from '@shared/money';
import { startOfLocalDate } from '@shared/time';
import { api, actionKey } from '../../api';
import { useSession } from '../../session';
import type { Assignment, FulfilmentView, IndentView, Person, SupplierOption, Vehicle } from '../../types';
import { Badge, ConfirmDialog, Dialog, Empty, ErrorBox, Field, Loading, RupeeInput, day, dt, inr, litres, rupeesToPaise, statusTone, today, useBusy } from '../../ui';
import { Icon, Ticket, fuelTone, indentTicket, plateText, rs } from '../../brand';

const FILTERS: [string, string][] = [['', 'All'], ['REQUESTED', 'Waiting'], ['APPROVED', 'Ready'], ['CLAIMED', 'Filling now'], ['FULFILLED', 'Filled'], ['CANCELLED,REVOKED,EXPIRED,REJECTED', 'Closed']];

export function IndentList() {
  const { can } = useSession();
  const [status, setStatus] = useState('');
  const q = useQuery({ queryKey: ['indents', status], queryFn: () => api.get<IndentView[]>(`/api/indents${status ? `?status=${status}` : ''}`) });
  return (
    <div className="stack">
      <h1>Indents</h1>
      <div className="filters" role="group" aria-label="Filter">
        {FILTERS.map(([v, l]) => (
          <button key={l} className={`btn small ${status === v ? 'primary' : ''}`} aria-pressed={status === v} onClick={() => setStatus(v)}>{l}</button>
        ))}
      </div>
      {q.isLoading ? <Loading /> : <ErrorBox error={q.error} />}
      {q.data && !q.data.length ? <Empty>No indents here.</Empty> : null}
      <ul className="tickets">
        {q.data?.map((i) => <IndentRow key={i.id} i={i} to={`/buyer/indents/${i.id}`} />)}
      </ul>
      {can('indent.issue') ? <Link className="fab" to="/buyer/indents/new"><Icon name="plus" size={24} stroke={2.6} />New indent</Link> : null}
    </div>
  );
}

export function IndentRow({ i, to, hideDriver }: { i: IndentView; to: string; hideDriver?: boolean }) {
  const notes = [
    i.status === 'FULFILLED' && i.ackStatus === 'PENDING' ? <Badge key="ack" tone="warn">{ACK_STATUS_LABELS.PENDING}</Badge> : null,
    i.disputeOpen ? <Badge key="dispute" tone="bad">Disputed</Badge> : null,
  ].filter(Boolean);
  return (
    <Ticket
      t={{ ...indentTicket(i, <>{i.number} · {dt(i.requestedAt)}</>), ...(hideDriver ? { driver: undefined } : {}), extra: notes.length ? <div className="row" style={{ gap: '.35rem', marginTop: '.2rem' }}>{notes}</div> : undefined }}
      to={to}
    />
  );
}

interface FormProps {
  mode: 'ISSUE' | 'REQUEST';
  fixedPersonId?: string;
  onDone: (indent: IndentView) => void;
}

const AMOUNT_STEP = 50_000n; // ₹500 in paise
const QUICK_AMOUNTS = ['1000', '2000', '5000'];

function rupeeText(paise: bigint) {
  return (paise / 100n).toString();
}

export function IndentForm({ mode, fixedPersonId, onDone }: FormProps) {
  const suppliers = useQuery({ queryKey: ['buyer-suppliers'], queryFn: () => api.get<SupplierOption[]>('/api/buyer/suppliers') });
  const people = useQuery({ queryKey: ['people'], queryFn: () => api.get<Person[]>('/api/people'), enabled: !fixedPersonId });
  const vehicles = useQuery({ queryKey: ['vehicles'], queryFn: () => api.get<Vehicle[]>('/api/vehicles') });
  const assignments = useQuery({ queryKey: ['assignments'], queryFn: () => api.get<Assignment[]>('/api/assignments') });
  const costCentres = useQuery({ queryKey: ['cost-centres'], queryFn: () => api.get<{ _id: string; code: string; name: string }[]>('/api/cost-centres') });
  const active = suppliers.data?.filter((s) => s.status === 'ACTIVE') ?? [];
  const [relId, setRelId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [personId, setPersonId] = useState(fixedPersonId ?? '');
  const [vehicleId, setVehicleId] = useState('');
  const [typeDriver, setTypeDriver] = useState(false);
  const [typeVehicle, setTypeVehicle] = useState(false);
  const [driverName, setDriverName] = useState('');
  const [vehicleRegistration, setVehicleRegistration] = useState('');
  const [fuelType, setFuelType] = useState('');
  const [limitType, setLimitType] = useState<'AMOUNT' | 'QUANTITY' | 'FULL_TANK'>('AMOUNT');
  const [amount, setAmount] = useState('');
  const [qty, setQty] = useState('');
  const [until, setUntil] = useState('');
  const [cc, setCc] = useState('');
  const [note, setNote] = useState('');
  const [exception, setException] = useState('');
  const [confirm, setConfirm] = useState(false);
  const { busy, error, run, setError } = useBusy();

  const rel = active.find((s) => s.relationshipId === relId) ?? (active.length === 1 ? active[0] : undefined);
  const loc = rel?.locations.find((l) => l.id === locationId) ?? (rel?.locations.length === 1 ? rel.locations[0] : undefined);
  const pid = fixedPersonId ?? personId;
  const myVehicles = useMemo(() => {
    const ids = new Set(assignments.data?.filter((a) => a.personId === pid).map((a) => a.vehicleId));
    return vehicles.data?.filter((v) => !fixedPersonId || ids.has(v.id)) ?? [];
  }, [assignments.data, vehicles.data, pid, fixedPersonId]);
  const vehicle = myVehicles.find((v) => v.id === vehicleId) ?? (fixedPersonId && myVehicles.length === 1 ? myVehicles[0] : undefined);
  const savedPeople = people.data?.filter((p) => p.status === 'ACTIVE' || p.id === pid) ?? [];
  const showVehicleInput = !fixedPersonId && !vehicle && (typeVehicle || !myVehicles.length);
  const showDriverInput = !fixedPersonId && !pid && (typeDriver || !savedPeople.length);
  const fuel = fuelType || (vehicle && loc?.fuelTypes.includes(vehicle.fuelType) ? vehicle.fuelType : '') || (loc?.fuelTypes.length === 1 ? loc.fuelTypes[0] : '');
  const price = fuel && loc ? loc.prices[fuel] : undefined;
  const amountPaise = rupeesToPaise(amount);
  let qtyMilli: bigint | null = null;
  try {
    qtyMilli = qty ? parseQuantity(qty) : null;
  } catch {
    qtyMilli = null;
  }
  const amountLitres = amountPaise && price ? Number(quantityToWire(maxQuantityForAmount(BigInt(amountPaise), BigInt(price)))) : null;
  const qtyRupees = qtyMilli && price ? amountForQuantity(qtyMilli, BigInt(price)) : null;
  const expiresAt = until ? new Date(startOfLocalDate(today()).getTime() + (Number(until.slice(0, 2)) * 60 + Number(until.slice(3, 5))) * 60_000).toISOString() : undefined;
  const typedVehicle = showVehicleInput ? vehicleRegistration.trim() : '';
  const typedDriver = showDriverInput ? driverName.trim() : '';
  const ready = rel && loc && (fixedPersonId ? pid && vehicle : pid || vehicle || typedDriver || typedVehicle.length >= 3) && fuel && (limitType === 'FULL_TANK' ? rel.fullTankCeilingPaise : limitType === 'AMOUNT' ? amountPaise && amountPaise !== '0' : qtyMilli && qtyMilli > 0n);
  const holdPaise = limitType === 'FULL_TANK' ? rel?.fullTankCeilingPaise : limitType === 'AMOUNT' ? amountPaise : qtyRupees?.toString();
  const available = rel?.summary?.position.availablePaise;
  const leftAfter = available && holdPaise ? BigInt(available) - BigInt(holdPaise) : null;

  const submit = () =>
    run(async () => {
      const k = actionKey(`indent-create:${rel!.relationshipId}:${pid}`);
      const body = {
        action: mode,
        supplierOrgId: rel!.supplierOrgId,
        locationId: loc!.id,
        personId: pid || undefined,
        vehicleId: vehicle?.id,
        identityMode: fixedPersonId ? undefined : 'DETAILS',
        driverName: !fixedPersonId && !pid ? typedDriver || undefined : undefined,
        vehicleRegistration: !fixedPersonId && !vehicle ? typedVehicle || undefined : undefined,
        category: 'FUEL',
        fuelType: fuel,
        limitType: limitType === 'FULL_TANK' ? 'AMOUNT' : limitType,
        fullTank: limitType === 'FULL_TANK' || undefined,
        maxAmountPaise: limitType === 'AMOUNT' ? amountPaise : undefined,
        maxQuantity: limitType === 'QUANTITY' ? quantityToWire(qtyMilli!) : undefined,
        expiresAt,
        costCentreId: cc || undefined,
        note: note || undefined,
        capExceptionReason: exception || undefined,
      };
      try {
        const indent = await api.post<IndentView>('/api/indents', body, { idempotencyKey: k.key });
        k.done();
        onDone(indent);
      } catch (e) {
        // A refused request (e.g. not enough credit) changes nothing; let the user edit and retry with a fresh key.
        const failure = e as { status?: number; code?: string };
        if (failure.status && failure.status < 500 && failure.code !== 'REQUEST_IN_PROGRESS') k.done();
        throw e;
      }
    });

  if (suppliers.isLoading || vehicles.isLoading) return <Loading />;
  if (!active.length) return <Empty>No active supplier yet.</Empty>;
  const capError = (error as { code?: string } | null)?.code === 'SPEND_CAP_EXCEEDED';
  const personName = people.data?.find((p) => p.id === pid)?.name ?? (fixedPersonId ? 'You' : typedDriver || 'Not specified');
  const plate = vehicle?.registrationNumber ?? (typedVehicle || undefined);
  const limitLabel = limitType === 'FULL_TANK' ? `Full tank · stop at ${rs(rel?.fullTankCeilingPaise)}` : limitType === 'AMOUNT' ? (amountPaise ? <>up to <b>{rs(amountPaise)}</b></> : 'choose amount') : qtyMilli ? <>up to <b>{quantityToWire(qtyMilli).replace(/\.?0+$/, '')} L</b></> : 'choose litres';
  const stepAmount = (dir: 1 | -1) => {
    const now = amountPaise ? BigInt(amountPaise) : 0n;
    const next = dir > 0 ? (now / AMOUNT_STEP + 1n) * AMOUNT_STEP : ((now - 1n) / AMOUNT_STEP) * AMOUNT_STEP;
    setAmount(next > 0n ? rupeeText(next) : '');
  };
  const stepQty = (dir: 1 | -1) => {
    const now = qtyMilli ?? 0n;
    const step = 5000n;
    const next = dir > 0 ? (now / step + 1n) * step : ((now - 1n) / step) * step;
    setQty(next > 0n ? (next / 1000n).toString() : '');
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); setError(null); setConfirm(true); }}>
      <div className="preview" aria-label="Indent preview">
        <Ticket
          as="div"
          t={{
            fuelType: fuel || undefined,
            plate,
            driver: pid || typedDriver ? personName : undefined,
            limit: limitLabel,
            top: <>{rel?.supplierName}{loc ? ` · ${loc.name}` : ''}</>,
            extra: <div className="ticket-extra">Today till {until ? clock12(until) : '11:59 pm'}</div>,
          }}
        />
      </div>

      <div className="stack" style={{ marginTop: '1rem' }}>
        {active.length > 1 ? (
          <ChipGroup label="Supplier">
            {active.map((s) => (
              <RadioChip key={s.relationshipId} name="supplier" checked={rel?.relationshipId === s.relationshipId} onChange={() => { setRelId(s.relationshipId); setLocationId(''); }}>
                {s.supplierName}{s.frozen ? ' (credit stopped)' : ''}
              </RadioChip>
            ))}
          </ChipGroup>
        ) : null}
        {rel && rel.locations.length > 1 ? (
          <ChipGroup label="Pump">
            {rel.locations.map((l) => (
              <RadioChip key={l.id} name="pump" checked={loc?.id === l.id} onChange={() => setLocationId(l.id)}>{l.name}</RadioChip>
            ))}
          </ChipGroup>
        ) : null}

        <ChipGroup label="Vehicle">
          {myVehicles.map((v) => (
            <RadioChip key={v.id} name="vehicle" plate checked={vehicle?.id === v.id} onChange={() => { setVehicleId(v.id); setTypeVehicle(false); setFuelType(''); }} onReclick={fixedPersonId ? undefined : () => setVehicleId('')}>
              {plateText(v.registrationNumber)}
            </RadioChip>
          ))}
          {!fixedPersonId && myVehicles.length ? (
            <button type="button" className={`chip dashed ${typeVehicle && !vehicle ? 'on' : ''}`} aria-label="Type another vehicle number" onClick={() => { setVehicleId(''); setTypeVehicle(true); }}>
              <Icon name="plus" size={20} />Other
            </button>
          ) : null}
        </ChipGroup>
        {fixedPersonId && !myVehicles.length ? <p className="alert warn">No vehicle is linked to you. Ask your company to make the indent with the vehicle number.</p> : null}
        {showVehicleInput ? (
          <Field label="Vehicle number"><input value={vehicleRegistration} onChange={(e) => setVehicleRegistration(e.target.value.toUpperCase())} maxLength={24} placeholder="TN 09 AB 1234" autoFocus={typeVehicle} /></Field>
        ) : null}

        {!fixedPersonId ? (
          <>
            <ChipGroup label="Driver">
              {savedPeople.map((p) => (
                <RadioChip key={p.id} name="driver" round checked={pid === p.id} onChange={() => { setPersonId(p.id); setTypeDriver(false); }} onReclick={() => setPersonId('')}>{p.name}</RadioChip>
              ))}
              {savedPeople.length ? (
                <button type="button" className={`chip round dashed ${typeDriver && !pid ? 'on' : ''}`} onClick={() => { setPersonId(''); setTypeDriver(true); }}>Type a name</button>
              ) : null}
            </ChipGroup>
            {showDriverInput ? (
              <Field label="Driver name"><input value={driverName} onChange={(e) => setDriverName(e.target.value)} maxLength={120} autoFocus={typeDriver} /></Field>
            ) : null}
          </>
        ) : null}

        {loc ? (
          <ChipGroup label="Fuel" three={loc.fuelTypes.length <= 3}>
            {loc.fuelTypes.map((f) => (
              <RadioChip key={f} name="fuel" checked={fuel === f} onChange={() => setFuelType(f)}>
                <span className={`dot fuel-${fuelTone(f)}`} />{FUEL_TYPE_LABELS[f as FuelType]}
              </RadioChip>
            ))}
          </ChipGroup>
        ) : null}

        <ChipGroup label="Limit by" three>
          <RadioChip name="limit" checked={limitType === 'AMOUNT'} onChange={() => setLimitType('AMOUNT')}>Rupees</RadioChip>
          <RadioChip name="limit" checked={limitType === 'QUANTITY'} onChange={() => setLimitType('QUANTITY')}>Litres</RadioChip>
          <RadioChip name="limit" checked={limitType === 'FULL_TANK'} onChange={() => setLimitType('FULL_TANK')}>Full tank</RadioChip>
        </ChipGroup>
        {limitType === 'AMOUNT' ? (
          <div>
            <div className="stepper">
              <button type="button" aria-label="Less" onClick={() => stepAmount(-1)}>−</button>
              <div className="mid">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span className="num" style={{ fontSize: '2.1rem' }}>₹</span>
                  <input aria-label="Maximum amount (₹)" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))} placeholder="0" style={{ width: `${Math.max(2, amount.length + 1)}ch` }} />
                </div>
                <div className="sub">{amountLitres !== null ? `about ${Math.floor(amountLitres)} litres` : price ? '' : 'choose fuel to see litres'}</div>
              </div>
              <button type="button" aria-label="More" onClick={() => stepAmount(1)}>+</button>
            </div>
            <div className="quick">
              {QUICK_AMOUNTS.map((a) => (
                <button key={a} type="button" className={`chip ${amount === a ? 'on' : ''}`} onClick={() => setAmount(a)}>{rs(`${a}00`)}</button>
              ))}
            </div>
          </div>
        ) : limitType === 'QUANTITY' ? (
          <div className="stepper">
            <button type="button" aria-label="Fewer litres" onClick={() => stepQty(-1)}>−</button>
            <div className="mid">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '.25rem' }}>
                <input aria-label="Maximum litres" inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value.replace(/[^\d.]/g, ''))} placeholder="0" style={{ width: `${Math.max(2, qty.length + 1)}ch` }} />
                <span className="num" style={{ fontSize: '1.6rem' }}>L</span>
              </div>
              <div className="sub">{qtyRupees !== null ? `about ${formatINR(qtyRupees, { paise: false })}` : ''}</div>
            </div>
            <button type="button" aria-label="More litres" onClick={() => stepQty(1)}>+</button>
          </div>
        ) : (
          <p className={rel?.fullTankCeilingPaise ? 'alert' : 'alert warn'}>
            {rel?.fullTankCeilingPaise ? `Fill the tank, stopping at ${rs(rel.fullTankCeilingPaise)}. Only the fuel actually filled is charged.` : 'Full tank is not set up yet. The supplier must propose a full-tank limit and your company must accept it.'}
          </p>
        )}

        <details>
          <summary>More options</summary>
          <div className="stack">
            <Field label="Valid until (optional)" hint="Every indent ends at midnight today. You can choose an earlier time."><input type="time" value={until} onChange={(e) => setUntil(e.target.value)} /></Field>
            {costCentres.data?.length ? (
              <Field label="Project / cost centre (optional)">
                <select value={cc} onChange={(e) => setCc(e.target.value)}>
                  <option value="">None</option>
                  {costCentres.data.map((c) => <option key={c._id} value={c._id}>{c.code} – {c.name}</option>)}
                </select>
              </Field>
            ) : null}
            <Field label="Note (optional)"><input value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} /></Field>
          </div>
        </details>
      </div>

      <div className="submit-bar">
        {leftAfter !== null ? <p className="left">{leftAfter >= 0n ? `Credit left after this: ${rs(leftAfter.toString())}` : `Not enough credit: short by ${rs((-leftAfter).toString())}`}</p> : null}
        <button className="btn primary block huge" disabled={!ready}>{mode === 'ISSUE' ? 'Approve and send indent' : 'Send request'}</button>
      </div>
      <ConfirmDialog
        open={confirm}
        title={mode === 'ISSUE' ? 'Approve this indent?' : 'Send this request?'}
        lines={[
          ['Driver', personName],
          ['Vehicle', plate ? plateText(plate) : 'Not specified'],
          ['Fuel', fuel ? FUEL_TYPE_LABELS[fuel as FuelType] : ''],
          ['Limit', limitType === 'FULL_TANK' ? `Full tank, stop at ${inr(rel?.fullTankCeilingPaise)}` : limitType === 'AMOUNT' ? `Up to ${inr(amountPaise)}` : litres(qty ? quantityToWire(qtyMilli ?? 0n) : null)],
          ['Pump', loc?.name ?? ''],
          ['Valid until', until ? `${clock12(until)} today` : 'Midnight today'],
        ]}
        confirmLabel={mode === 'ISSUE' ? 'Approve and get code' : 'Send request'}
        busy={busy}
        error={error}
        onConfirm={() => void submit()}
        onClose={() => setConfirm(false)}
      >
        {mode === 'ISSUE' && capError ? (
          <Field label="One-time exception reason" hint="Above a company spending limit. Your name and reason are recorded.">
            <input value={exception} onChange={(e) => setException(e.target.value)} />
          </Field>
        ) : null}
      </ConfirmDialog>
    </form>
  );
}

function clock12(hhmm: string) {
  const h = Number(hhmm.slice(0, 2));
  return `${h % 12 || 12}:${hhmm.slice(3, 5)} ${h < 12 ? 'am' : 'pm'}`;
}

function ChipGroup({ label, three, children }: { label: string; three?: boolean; children: React.ReactNode }) {
  return (
    <div role="radiogroup" aria-label={label}>
      <span className="form-label">{label}</span>
      <div className={`chips ${three ? 'three' : ''}`}>{children}</div>
    </div>
  );
}

function RadioChip({ name, checked, onChange, onReclick, round, plate, children }: { name: string; checked: boolean; onChange: () => void; onReclick?: () => void; round?: boolean; plate?: boolean; children: React.ReactNode }) {
  return (
    <label className={`chip ${round ? 'round' : ''} ${plate ? 'plate-chip' : ''} ${checked ? 'on' : ''}`} onClick={(e) => { if (checked && onReclick) { e.preventDefault(); onReclick(); } }}>
      <input type="radio" name={name} checked={checked} onChange={onChange} />
      {children}
    </label>
  );
}

export function NewIndent() {
  const nav = useNavigate();
  const qc = useQueryClient();
  return (
    <div>
      <div className="page-head">
        <Link className="back" to="/buyer" aria-label="Back"><Icon name="back" size={24} stroke={2.4} /></Link>
        <div>
          <h1>New indent</h1>
          <p className="muted" style={{ margin: 0 }}>Lets one vehicle fill fuel once, today</p>
        </div>
      </div>
      <IndentForm mode="ISSUE" onDone={(i) => { void qc.invalidateQueries(); nav(`/buyer/indents/${i.id}`); }} />
    </div>
  );
}

interface IndentDetailData {
  indent: IndentView;
  locationName?: string;
  supplierName?: string;
  fulfilment: FulfilmentView | null;
  approvals: { decision: string; by: string; at: string; reason?: string }[];
  history: { action: string; at: string; by?: string; reason?: string }[];
}

export function CodeCard({ indentId, canResend }: { indentId: string; canResend: boolean }) {
  const q = useQuery({ queryKey: ['token', indentId], queryFn: () => api.get<{ qrSvg: string; shortCode: string; expiresAt: string; shareText: string; whatsappTo?: { name: string; number: string }; smsAvailable?: boolean }>(`/api/indents/${indentId}/token`), staleTime: 0 });
  const [sent, setSent] = useState(false);
  const [copied, setCopied] = useState(false);
  if (q.isLoading) return <Loading label="Getting code…" />;
  if (q.error) return <ErrorBox error={q.error} />;
  const d = q.data!;
  return (
    <div className="card" style={{ textAlign: 'center' }}>
      <h2>Send to the driver</h2>
      <p className="muted">Send the code and QR link on WhatsApp to the driver, or to the pump.</p>
      <div className="row" style={{ justifyContent: 'center' }}>
        {d.whatsappTo ? <a className="btn primary" href={`https://wa.me/${d.whatsappTo.number}?text=${encodeURIComponent(d.shareText)}`} target="_blank" rel="noreferrer">Send to {d.whatsappTo.name.split(' (')[0]} on WhatsApp</a> : null}
        <a className={d.whatsappTo ? 'btn' : 'btn primary'} href={`https://wa.me/?text=${encodeURIComponent(d.shareText)}`} target="_blank" rel="noreferrer">{d.whatsappTo ? 'Send to someone else' : 'Send on WhatsApp'}</a>
        <button className="btn" onClick={() => void navigator.clipboard?.writeText(d.shareText).then(() => setCopied(true))}>{copied ? 'Copied' : 'Copy message'}</button>
      </div>
      <div className="qr" dangerouslySetInnerHTML={{ __html: d.qrSvg }} aria-label="QR code for the pump" role="img" />
      <p>Or tell the attendant this code:</p>
      <div className="code" aria-label={`Code ${d.shortCode}`}>{d.shortCode}</div>
      <p className="muted">Valid until {dt(d.expiresAt)}. Works once, only at the chosen pump.</p>
      {canResend && d.smsAvailable ? <button className="btn small" disabled={sent} onClick={() => void api.post(`/api/indents/${indentId}/resend-code`).then(() => setSent(true))}>{sent ? 'SMS queued' : 'Send code again by SMS'}</button> : null}
    </div>
  );
}

export function FulfilmentCard({ f, canAccountsAck, canDispute, onChanged }: { f: FulfilmentView; canAccountsAck: boolean; canDispute: boolean; onChanged: () => void }) {
  const [ackOpen, setAckOpen] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [note, setNote] = useState('');
  const [amt, setAmt] = useState('');
  const { busy, error, run } = useBusy();
  const line = f.lines[0];
  return (
    <div className="card">
      <div className="row between">
        <h2>Supplied {f.mode === 'MANUAL' ? <Badge tone="warn">Recorded by hand</Badge> : null}</h2>
        <Badge tone={statusTone(f.ack.status)}>{ACK_STATUS_LABELS[f.ack.status]}</Badge>
      </div>
      <div className="hero-amount">{inr(f.totalPaise)}</div>
      <p>{litres(line.quantity, line.unit)} {line.description} at {inr(line.unitPricePaise)}/{line.unit} · {dt(f.occurredAt)}</p>
      {f.authorisedPaise ? <p className="muted">Approved up to {inr(f.authorisedPaise)}; the rest was released.</p> : null}
      {f.odometer ? <p>Odometer: {f.odometer.reading} km {f.odometer.exception ? <Badge tone="warn">lower reading allowed: {f.odometer.exception.reason}</Badge> : null}</p> : null}
      <p className="muted">Supply confirmed by {f.supplierConfirmation.by} · {f.number}</p>
      {!['PENDING', 'NOT_DUE', 'NOT_REQUIRED'].includes(f.ack.status) ? <p className="muted">Confirmed by {f.ack.by} ({f.ack.method?.toLowerCase().replace('_', ' ')}) {f.ack.note ? `· ${f.ack.note}` : ''}</p> : null}
      {f.evidenceIds.length ? <p>{f.evidenceIds.map((id, n) => <a key={id} href={`/api/attachments/${id}`} target="_blank" rel="noreferrer" onClick={(e) => { e.preventDefault(); void openAttachment(id); }}>Photo {n + 1} </a>)}</p> : null}
      {f.disputeOpen ? <div className="alert warn">A dispute is open on this supply. The amount stays due until a correction is agreed.</div> : null}
      <div className="row">
        {canAccountsAck && f.ack.status === 'PENDING' ? <button className="btn" onClick={() => setAckOpen(true)}>Accept as company accounts</button> : null}
        {canDispute && !f.disputeOpen ? <button className="btn" onClick={() => setDisputeOpen(true)}>Raise a dispute</button> : null}
      </div>
      <ConfirmDialog open={ackOpen} title="Accept this supply for the company?" lines={[['Amount', inr(f.totalPaise)], ['Quantity', litres(line.quantity, line.unit)]]} confirmLabel="Accept" busy={busy} error={error} onClose={() => setAckOpen(false)} onConfirm={() => void run(async () => { await api.post(`/api/fulfilments/${f.id}/ack/accounts`, { note }); setAckOpen(false); onChanged(); })}>
        <Field label="Note" hint="e.g. checked with dispenser slip"><input value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      </ConfirmDialog>
      <Dialog open={disputeOpen} title="Raise a dispute" onClose={() => setDisputeOpen(false)}>
        <p className="muted">The supply stays on record. The supplier will review and may give a correction.</p>
        <Field label="Disputed amount (₹)"><RupeeInput value={amt} onChange={setAmt} /></Field>
        <Field label="What is wrong?"><textarea value={note} onChange={(e) => setNote(e.target.value)} /></Field>
        <ErrorBox error={error} />
        <button className="btn primary" disabled={busy || !note.trim()} onClick={() => void run(async () => { await api.post('/api/disputes', { subjectKind: 'FULFILMENT', subjectId: f.id, disputedAmountPaise: rupeesToPaise(amt) ?? '0', reason: note, evidenceIds: [] }); setDisputeOpen(false); onChanged(); })}>Send dispute</button>
      </Dialog>
    </div>
  );
}

export async function openAttachment(id: string) {
  const res = await fetch(`/api/attachments/${id}`, { headers: { 'X-Org-Id': localStorage.getItem('si.activeOrg') ?? '' }, credentials: 'include' });
  if (!res.ok) return alert('This file cannot be opened.');
  const blob = await res.blob();
  window.open(URL.createObjectURL(blob), '_blank', 'noopener');
}

export function IndentDetail() {
  const { id } = useParams();
  const { can } = useSession();
  const qc = useQueryClient();
  const nav = useNavigate();
  const q = useQuery({ queryKey: ['indent', id], queryFn: () => api.get<IndentDetailData>(`/api/indents/${id}`), refetchInterval: 15_000 });
  const [dialog, setDialog] = useState<'approve' | 'reject' | 'revoke' | 'revise' | 'odometer' | null>(null);
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState('');
  const { busy, error, run, setError } = useBusy();
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const { indent: i, fulfilment, history, locationName, supplierName } = q.data!;
  const refresh = () => qc.invalidateQueries();
  const close = () => { setDialog(null); setReason(''); setAmount(''); setError(null); };
  const post = (path: string, body: unknown, key?: string) =>
    run(async () => {
      const k = key ? actionKey(key) : null;
      const r = await api.post<IndentView>(`/api/indents/${i.id}/${path}`, body, k ? { idempotencyKey: k.key } : undefined);
      k?.done();
      close();
      await refresh();
      if (path === 'revise') nav(`/buyer/indents/${r.id}`);
    });

  return (
    <div className="stack">
      <div className="card">
        <div className="row between">
          <h1>{i.person.name}{i.vehicle ? ` · ${i.vehicle.registrationNumber}` : ''}</h1>
          <Badge tone={statusTone(i.status)}>{i.statusLabel}</Badge>
        </div>
        <div className="summary">
          <dl>
            <dt>Indent</dt><dd>{i.number}{i.revision > 1 ? ` (revision ${i.revision})` : ''}</dd>
            <dt>Supplier</dt><dd>{supplierName} · {locationName}</dd>
            <dt>Fuel</dt><dd>{i.fuelType ? FUEL_TYPE_LABELS[i.fuelType as FuelType] : i.category}</dd>
            {i.fullTank ? <><dt>Instruction</dt><dd>Fill the tank, stopping at the approved ceiling.</dd></> : null}
            <dt>Up to</dt><dd>{i.limitType === 'AMOUNT' ? inr(i.maxAmountPaise) : `${litres(i.maxQuantity, i.unit)}${i.estimatedUnitPricePaise ? ` (≈ ${inr(i.reservedPaise)})` : ''}`}</dd>
            {i.reservedPaise && ['APPROVED', 'CLAIMED'].includes(i.status) ? <><dt>Held from credit</dt><dd>{inr(i.reservedPaise)}</dd></> : null}
            <dt>Valid until</dt><dd>{dt(i.expiresAt)}</dd>
            <dt>Asked by</dt><dd>{i.requestedBy} · {dt(i.requestedAt)}</dd>
            {i.approvedBy ? <><dt>Approved by</dt><dd>{i.approvedBy} · {dt(i.approvedAt)}</dd></> : null}
            {i.closedReason ? <><dt>Closed</dt><dd>{i.closedReason}</dd></> : null}
            {i.capException ? <><dt>Limit exception</dt><dd>{i.capException.reason} ({i.capException.approvedBy})</dd></> : null}
          </dl>
        </div>
        {i.supersededByIndentId ? <p><Link to={`/buyer/indents/${i.supersededByIndentId}`}>See the newer version →</Link></p> : null}
        {i.codeDelivery && i.status === 'APPROVED' ? (
          i.codeDelivery.status === 'SENT' ? <p className="muted">Code sent by SMS to {i.person.phoneMasked}.</p> : <div className="alert info">Send the code to the driver on WhatsApp below.</div>
        ) : null}
        <div className="row">
          {can('indent.approve') && ['REQUESTED', 'DRAFT'].includes(i.status) ? <><button className="btn primary" onClick={() => setDialog('approve')}>Approve</button><button className="btn" onClick={() => setDialog('reject')}>Reject</button></> : null}
          {can('indent.approve') && ['APPROVED', 'REQUESTED'].includes(i.status) ? <button className="btn" onClick={() => setDialog('revise')}>Change amount</button> : null}
          {can('indent.revoke') && i.status === 'APPROVED' ? <button className="btn danger" onClick={() => setDialog('revoke')}>Stop this indent</button> : null}
          {can('indent.approve') && ['APPROVED', 'CLAIMED'].includes(i.status) ? <button className="btn ghost" onClick={() => setDialog('odometer')}>Allow lower odometer</button> : null}
        </div>
      </div>
      {i.status === 'APPROVED' && can('indent.issue') ? <CodeCard indentId={i.id} canResend={Boolean(i.personId)} /> : null}
      {i.status === 'CLAIMED' ? <div className="alert info">The pump has started filling. Supply will be recorded shortly; this can no longer be stopped.</div> : null}
      {fulfilment ? <FulfilmentCard f={fulfilment} canAccountsAck={can('ack.accounts')} canDispute={can('buyer.disputes.raise')} onChanged={() => void refresh()} /> : null}
      <div className="card">
        <h2>History</h2>
        <ul className="list">
          {history.map((h, n) => <li key={n}><strong>{h.action.replace(/[._]/g, ' ')}</strong> · {h.by ?? 'system'} · <small>{dt(h.at)}</small>{h.reason ? <div><small>{h.reason}</small></div> : null}</li>)}
        </ul>
      </div>

      <ConfirmDialog open={dialog === 'approve'} title="Approve this indent?" lines={[['Driver', i.person.name], [i.fullTank ? 'Fill the tank, ceiling' : 'Up to', i.limitType === 'AMOUNT' ? inr(i.maxAmountPaise) : litres(i.maxQuantity)]]} confirmLabel="Approve and get code" busy={busy} error={error} onClose={close} onConfirm={() => void post('approve', { capExceptionReason: reason || undefined }, `approve:${i.id}`)}>
        {(error as { code?: string } | null)?.code === 'SPEND_CAP_EXCEEDED' ? <Field label="One-time exception reason"><input value={reason} onChange={(e) => setReason(e.target.value)} /></Field> : null}
      </ConfirmDialog>
      <ConfirmDialog open={dialog === 'reject'} title="Reject this request?" lines={[['Driver', i.person.name]]} confirmLabel="Reject" danger busy={busy} error={error} onClose={close} onConfirm={() => void post('reject', { reason })}>
        <Field label="Reason"><input value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
      </ConfirmDialog>
      <ConfirmDialog open={dialog === 'revoke'} title="Stop this indent?" lines={[['Driver', i.person.name], ['Held from credit', inr(i.reservedPaise)]]} confirmLabel="Stop indent" danger busy={busy} error={error} onClose={close} onConfirm={() => void post('revoke', { reason }, `revoke:${i.id}`)}>
        <p className="muted">The code stops working and the amount comes back to your available credit. If the pump has already started filling, it cannot be stopped.</p>
        <Field label="Reason"><input value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
      </ConfirmDialog>
      <ConfirmDialog open={dialog === 'revise'} title="Change the approved amount" lines={[['Now', i.limitType === 'AMOUNT' ? inr(i.maxAmountPaise) : litres(i.maxQuantity)], ['New', inr(rupeesToPaise(amount))]]} confirmLabel="Approve new amount" busy={busy} error={error} onClose={close} onConfirm={() => void post('revise', { limitType: 'AMOUNT', maxAmountPaise: rupeesToPaise(amount), reason: reason || 'Amount changed' }, `revise:${i.id}`)}>
        <p className="muted">A new code is sent; the old code stops working. A credit check runs again.</p>
        <Field label="New maximum (₹)"><RupeeInput value={amount} onChange={setAmount} /></Field>
        <Field label="Reason"><input value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
      </ConfirmDialog>
      <ConfirmDialog open={dialog === 'odometer'} title="Allow a lower odometer reading?" lines={[['Vehicle', i.vehicle?.registrationNumber ?? '']]} confirmLabel="Allow" busy={busy} error={error} onClose={close} onConfirm={() => void post('odometer-exception', { reason })}>
        <Field label="Reason" hint="e.g. odometer replaced at workshop"><input value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
      </ConfirmDialog>
      <p className="muted"><small>Day: {day(i.requestedAt)}</small></p>
    </div>
  );
}
